import { isNil } from "@clipboard-health/util-ts";
import { post } from "@src/appV2/api/api";
import { getAuthHeaderValue } from "@src/appV2/Auth/cognito/getAuthorizationHeader";
import {
  useMutation,
  type UseMutationOptions,
  type UseMutationResult,
} from "@tanstack/react-query";
import { type AxiosError } from "axios";
import { z } from "zod";

import { environmentConfig } from "../environment";
import { APP_V2_APP_EVENTS } from "../lib";

const zendeskSsoTokenSchema = z.object({
  jwt: z.string(),
});

type ZendeskSsoTokenResponse = z.infer<typeof zendeskSsoTokenSchema>;

export async function getZendeskSSoToken(): Promise<ZendeskSsoTokenResponse | undefined> {
  const authHeaderValue = await getAuthHeaderValue();

  if (isNil(authHeaderValue)) {
    return undefined;
  }

  const response = await post({
    url: `${environmentConfig.REACT_APP_ZENDESK_JWT_MICROSERVICE_BASE_URL}/zendesk/jwt/`,
    data: {
      user_token: authHeaderValue,
    },
    responseSchema: zendeskSsoTokenSchema,
  });

  return response.data;
}

export function useZendeskSsoToken(
  options: UseMutationOptions<ZendeskSsoTokenResponse | undefined, AxiosError> = {}
): UseMutationResult<ZendeskSsoTokenResponse | undefined, AxiosError, void> {
  return useMutation({
    mutationFn: async () => {
      return await getZendeskSSoToken();
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.ZENDESK_SSO_ERROR_GETTING_TOKEN,
    },
    ...options,
  });
}
